import { useState } from 'react'
import { Icon } from './Icon'
import { provisionUserGroup } from '../api/client'
import chipStyles from './ChipInput.module.css'
import styles from './PrefixInput.module.css'

type ProvisionState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; prUrl: string }
  | { status: 'conflict'; message: string; fileUrl: string | null }
  | { status: 'error'; message: string }

interface Props {
  channelId: string
  sessionToken: string
  value: string
  onChange: (value: string) => void
  // During onboarding: pass the current author team slug (committed or in-progress input).
  // From settings: omit — backend uses already-saved authorTeamSlugs.
  authorTeamSlug?: string
  // Show a warning when no author team is available yet
  showAuthorTeamWarning?: boolean
}

export function PrefixInput({ channelId, sessionToken, value, onChange, authorTeamSlug, showAuthorTeamWarning }: Props) {
  const [provision, setProvision] = useState<ProvisionState>({ status: 'idle' })

  async function handleProvision() {
    setProvision({ status: 'loading' })
    try {
      const { prUrl } = await provisionUserGroup(sessionToken, channelId, value, authorTeamSlug)
      setProvision({ status: 'success', prUrl })
    } catch (err: unknown) {
      const e = err as { status?: number; message?: string; field?: string; fileUrl?: string | null }
      if (e?.status === 409) {
        setProvision({ status: 'conflict', message: e.message ?? 'Already provisioned.', fileUrl: e.fileUrl ?? null })
      } else {
        setProvision({ status: 'error', message: e.message ?? 'Something went wrong. Please try again.' })
      }
    }
  }

  const canProvision = value.trim().length > 0

  return (
    <div className={chipStyles.field}>
      <label className={chipStyles.label}>Team prefix</label>
      <div className={`${chipStyles.inputWrap} ${chipStyles.inputWrapSingle}`}>
        <input
          type="text"
          value={value}
          onChange={e => {
            onChange(e.target.value)
            setProvision({ status: 'idle' })
          }}
          placeholder="e.g. ent-away"
        />
      </div>
      <div className={chipStyles.hint}>
        Slack user groups will be provisioned starting with this prefix, one per repo.
      </div>

      <div className={styles.provisionRow}>
        {showAuthorTeamWarning && value.trim().length > 0 && (
          <div className={styles.provisionWarning}>
            <Icon name="info" size="sm" />
            Add at least one author-watch team before provisioning.
          </div>
        )}
        <button
          className={styles.provisionBtn}
          onClick={handleProvision}
          disabled={!canProvision || provision.status === 'loading'}
        >
          {provision.status === 'loading' ? (
            <><Icon name="hourglass_empty" size="sm" /> Provisioning…</>
          ) : (
            <><Icon name="auto_awesome" size="sm" /> Provision user groups</>
          )}
        </button>

        {provision.status === 'success' && (
          <div className={styles.provisionSuccess}>
            <Icon name="check_circle" size="sm" />
            PR raised —{' '}
            <a href={provision.prUrl} target="_blank" rel="noopener noreferrer">view pull request</a>
            {' '}· feel free to review and tune the algorithm input parameters
          </div>
        )}

        {provision.status === 'conflict' && (
          <div className={styles.provisionConflict}>
            <Icon name="info" size="sm" />
            <span>
              {provision.message}
              {provision.fileUrl && (
                <>{' '}<a href={provision.fileUrl} target="_blank" rel="noopener noreferrer">View existing file</a></>
              )}
            </span>
          </div>
        )}

        {provision.status === 'error' && (
          <div className={styles.provisionError}>
            <Icon name="error_outline" size="sm" />
            {provision.message}
          </div>
        )}
      </div>
    </div>
  )
}
